
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { apiRequest } from '@/utils/request';

import { OrganizationForm } from '@/pages/Signup/Content/Organization';
import { OrganizationTypeForm } from '@/pages/Signup/Content/OrganizationType';
import Plans from '@/pages/Plans';
import { SubmissionConfirmation } from '@/pages/Signup/Content/SubmissionConfirmation';
import { TermsForm } from '@/pages/Signup/Content/Terms';
import { UserForm } from '@/pages/Signup/Content/User';
import { Wizard } from '@/components/business/Wizard';
import { Backdrop, Link, Surface, Text } from '@/components/ui';
import { NavigationService } from '@/utils/navigation';

const buildSchema = () => z.object({
    firstName: z.string().min(1, i18next.t('form_validations.users.first_name_required')),
    lastName: z.string().min(1, i18next.t('form_validations.users.last_name_required')),
    email: z.string().email(i18next.t('form_validations.users.email_invalid')),
    telephone: z.string().regex(/^\+[0-9]{1,4}( ?[0-9]){8,14}$/, i18next.t('form_validations.users.telephone_invalid')),
    organizationName: z.string().min(1, i18next.t('form_validations.organization.organization_name_required')),
    country: z.string().min(1, i18next.t('form_validations.organization.country_required')),
    organizationType: z.string().min(1),
    acceptedTerms: z.literal(true),
    marketing: z.boolean()
});

const SignupPage = () => {
    const form = useForm({
        resolver: zodResolver(buildSchema()),
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            telephone: '',
            organizationName: '',
            country: '',
            organizationType: '',
            acceptedTerms: false,
            marketing: false
        },
        mode: 'onBlur'
    });

    const steps = [
        {
            Component: UserForm,
            title: i18next.t('signup.register_user'),
            description: i18next.t('signup.register_user_desc'),
            isValid: data => Boolean(data.firstName?.trim())
                && Boolean(data.lastName?.trim())
                && Boolean(data.email?.trim())
                && Boolean(data.telephone?.trim())
        },
        {
            Component: OrganizationForm,
            title: i18next.t('signup.register_organization'),
            description: i18next.t('signup.register_organization_desc'),
            isValid: data => Boolean(data.organizationName?.trim()) && Boolean(data.country)
        },
        {
            Component: OrganizationTypeForm,
            title: i18next.t('signup.type_of_organization'),
            description: i18next.t('signup.type_of_organization_desc'),
            isValid: data => Boolean(data.organizationType)
        },
        {
            Component: () => <Plans organization={null}/>,
            title: i18next.t('signup.subscription_plan'),
            description: i18next.t('signup.subscription_plan_desc')
        },
        {
            Component: TermsForm,
            title: i18next.t('signup.terms_and_conditions'),
            description: i18next.t('signup.terms_and_conditions_desc'),
            isValid: data => data.acceptedTerms === true
        }
    ];

    const successStep = { Component: SubmissionConfirmation, title: i18next.t('signup.well_done'), description: i18next.t('signup.you_are_registered') };

    const handleBackToLogin = () => {
        NavigationService.navigateTo('login', { trigger: true, replace: true });
    };

    const handleSignupComplete = () => {
        window.location.href = 'https://www.wisemetering.com';
    };

    const handleSubmit = async (data) => {
        try {
            const submitData = {
                country: data.country,
                email: data.email,
                marketing: data.marketing,
                name: `${data.firstName} ${data.lastName}`,
                organization_name: data.organizationName,
                organization_type: data.organizationType,
                telephone: data.telephone
            };
            const response = await apiRequest('/leads', 'POST', submitData);
            return response;
        } catch (err) {
            const errorMessage = err.response?.data?.error?.capitalize?.() || i18next.t('alert_boxes.error_occurred');
            throw new Error(errorMessage, { cause: err });
        }
    };

    return (
        <div className="tw-relative tw-h-screen tw-w-screen tw-overflow-y-auto">
            {/* Fixed background layers */}
            <img
                src="/images/placeholder_wide.webp"
                alt=""
                aria-hidden="true"
                className="tw-fixed tw-top-0 tw-left-0 tw-w-full tw-h-1/2 tw-object-cover tw-object-center"
            />
            <div className="tw-fixed tw-bottom-0 tw-left-0 tw-w-full tw-h-1/2 tw-bg-surface"/>

            {/* Login link — fixed, behind overlay (dimmed, not clickable) */}
            <div className="tw-fixed tw-bottom-0 tw-inset-x-0 tw-mx-auto tw-w-full tw-max-w-modal tw-flex tw-items-center tw-justify-center tw-h-modal-band">
                <Text variant="body">
                    {i18next.t('signup.already_have_account')}
                    <span className="tw-ml-2">
                        <Link
                            onClick={handleBackToLogin}
                            text={i18next.t('signup.login_here')}
                            variant="body"
                        />
                    </span>
                </Text>
            </div>

            {/* Overlay — between backgrounds and content */}
            <div className="tw-fixed tw-inset-0 tw-z-lift">
                <Backdrop />
            </div>

            {/* Flow content: scrolls with the outer container; logo scrolls off when wizard is tall; login stays fixed */}
            <div className="tw-relative tw-z-modal tw-flex tw-min-h-screen tw-flex-col tw-items-center">
                {/* Logo band (in flow, scrolls with content) */}
                <div className="tw-w-full tw-max-w-modal tw-h-modal-band tw-flex tw-items-center tw-justify-start tw-pointer-events-none tw-shrink-0">
                    <img src="/images/wisemetering/negative-logo-horizontal.png" alt="Logo" className="tw-h-20"/>
                </div>

                {/* Wizard: flex-1 centres it when small; grows naturally when tall */}
                <div className="tw-w-full tw-flex-1 tw-flex tw-items-center tw-justify-center tw-px-4 tw-py-4">
                    <div className="tw-w-full tw-max-w-modal">
                        <Surface>
                            <Wizard
                                form={form}
                                onCancel={handleBackToLogin}
                                onClose={handleSignupComplete}
                                onSubmit={handleSubmit}
                                steps={steps}
                                successStep={successStep}
                            />
                        </Surface>
                    </div>
                </div>

                {/* Bottom spacer matching the fixed login band so the wizard never underlaps it */}
                <div className="tw-h-modal-band tw-shrink-0"/>
            </div>
        </div>
    );
};

export default SignupPage;
